import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, PieChart as PieIcon, TrendingDown, TrendingUp, Scale } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { CAT_MAP, fmtMoney } from "@/lib/expenseConstants";
import { UserAvatar } from "@/components/Avatars";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const PIE_COLORS = ["#1A73E8", "#D93025", "#1E8E3E", "#F9AB00", "#9D4CDD", "#FF7043", "#26A69A", "#EC407A"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const shiftMonth = (month, delta) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (month) => {
  const [y, m] = month.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

export const ReportTab = ({ workspaceId }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);

  const { data: report, isLoading } = useQuery({
    queryKey: ["expenses-report", workspaceId, month],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/expenses-report?month=${month}`)).data,
  });

  if (isLoading || !report) return <Skeleton className="h-72 rounded-xl" />;

  const cur = report.currency;
  const pieData = report.by_category.filter((c) => c.expense > 0)
    .map((c) => ({ name: `${CAT_MAP[c.category]?.emoji} ${CAT_MAP[c.category]?.label}`, value: c.expense }));
  const maxPerson = Math.max(...report.by_person.map((p) => p.expense), 1);
  const empty = report.by_category.length === 0;

  return (
    <div className="space-y-5" data-testid="report-tab">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="report-prev-month">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="report-next-month">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="font-heading font-semibold" data-testid="report-month-label">{monthLabel(month)}</span>
        {report.has_other_currencies && (
          <span className="text-[11px] text-muted-foreground ml-auto">Mostrando solo {cur} (hay registros en otras monedas)</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Gastos", value: report.totals.expense, icon: TrendingDown, color: "#D93025", testId: "report-total-expense" },
          { label: "Ingresos", value: report.totals.income, icon: TrendingUp, color: "#1E8E3E", testId: "report-total-income" },
          { label: "Neto", value: report.totals.net, icon: Scale, color: report.totals.net >= 0 ? "#1E8E3E" : "#D93025", testId: "report-total-net" },
        ].map((t) => (
          <Card key={t.label} className="p-4" data-testid={t.testId}>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <t.icon className="h-3.5 w-3.5" style={{ color: t.color }} /> {t.label}
            </div>
            <p className="font-heading font-bold text-lg sm:text-xl" style={{ color: t.color }}>
              {t.label === "Neto" && t.value > 0 ? "+" : ""}{fmtMoney(t.value, cur)}
            </p>
          </Card>
        ))}
      </div>

      {empty ? (
        <Card className="p-12 text-center border-dashed">
          <PieIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Sin movimientos este mes.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          <Card className="p-5 space-y-3" data-testid="report-category-chart">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Gastos por categoría</h3>
            {pieData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Sin gastos este mes.</p>
            ) : (
              <>
                <div className="h-52 min-h-[208px]" key={month}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                        {pieData.map((p, i) => <Cell key={p.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMoney(v, cur)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  {pieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="flex-1 truncate">{d.name}</span>
                      <span className="font-medium">{fmtMoney(d.value, cur)}</span>
                      <span className="text-muted-foreground w-10 text-right">
                        {Math.round((d.value / report.totals.expense) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card className="p-5 space-y-3" data-testid="report-person-ranking">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Ranking del mes por persona</h3>
            <div className="space-y-3">
              {report.by_person.map((p, i) => (
                <div key={p.profile_id} className="space-y-1" data-testid={`report-person-${p.profile_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}º</span>
                    <UserAvatar name={p.name} color={p.avatar_color} src={p.avatar_url} size="sm" className="!h-6 !w-6 !text-[10px]" />
                    <span className="text-sm font-medium flex-1 truncate">{p.name}</span>
                    <span className="text-sm font-semibold">{fmtMoney(p.expense, cur)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary ml-6 overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${(p.expense / maxPerson) * 100}%` }} />
                  </div>
                  {p.income > 0 && (
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 ml-6">+{fmtMoney(p.income, cur)} en ingresos</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
